import { ChangeEvent, ReactElement, useEffect, useState } from 'react';
import { Typography } from '@weareopenr/component-library';
import classNames from 'classnames/bind';
import styles from './MenuSetImageField.module.scss';

const cx = classNames.bind(styles);

const MAX_IMAGE_BYTES = 6 * 1024 * 1024;
export const IMAGE_TOO_LARGE_ERROR = 'The image must be 6MB or smaller';

// React 17 has no useId; a counter keeps the label/input pairing unique per field.
let instanceCounter = 0;

export interface ISavedImage {
  url: string;
  name: string;
}

interface IMenuSetImageFieldProps {
  title: string;
  savedImage: ISavedImage | null;
  newImage: File | null;
  onChange: (image: File) => void;
}

/**
 * The menu set image as a card showing its thumbnail and file name; clicking the card picks a replacement.
 * There's no remove action: an image can be replaced but not cleared.
 */
export const MenuSetImageField = ({ title, savedImage, newImage, onChange }: IMenuSetImageFieldProps): ReactElement => {
  const [inputId] = useState(() => {
    instanceCounter += 1;
    return `menu-set-image-input-${instanceCounter}`;
  });
  const [error, setError] = useState<string | null>(null);
  const [newImageUrl, setNewImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!newImage) return setNewImageUrl(null);
    const url = URL.createObjectURL(newImage);
    setNewImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [newImage]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) return setError(IMAGE_TOO_LARGE_ERROR);
    setError(null);
    onChange(file);
  };

  const thumbnailUrl = newImage ? newImageUrl : savedImage?.url;
  const fileName = newImage?.name ?? savedImage?.name;

  return (
    <div className={styles['field']}>
      <Typography size="medium" weight="semi-bold">
        {title}
      </Typography>
      <input
        id={inputId}
        type="file"
        accept="image/png, image/jpeg"
        aria-label={fileName ? `Replace ${title.replace(/:$/, '').toLowerCase()}` : 'Upload image'}
        className={styles['hidden-input']}
        onChange={handleChange}
        data-testid="menu-set-image-input"
      />
      <label htmlFor={inputId} className={cx('card', { empty: !fileName })}>
        {fileName ? (
          <>
            {thumbnailUrl && <img src={thumbnailUrl} alt="" className={styles['thumbnail']} />}
            <Typography className={styles['file-name']}>{fileName}</Typography>
          </>
        ) : (
          <Typography className={styles['file-name']}>Upload Image</Typography>
        )}
      </label>
      {error && (
        <Typography size="small" className={styles['error']}>
          {error}
        </Typography>
      )}
    </div>
  );
};

export default MenuSetImageField;
